import { FiSearch } from 'react-icons/fi';

export const keywordIdentificationData = {
  courseId: 'keyword-identification',
  courseType: 'reading-course',
  title: 'Keyword Identification',
  icon: FiSearch,
  overview: {
    impact: 'Find the right paragraph faster and reduce wasted reading time',
    focus: 'Locating stable keywords and matching paraphrases',
    uses: 'Reading speed, question targeting, evidence search',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'What Counts as a Strong Keyword',
      content:
        '<p>Good keywords are words that help you <strong>find</strong> the right place in the passage. The best ones are usually hard to paraphrase or easy to spot visually.</p>',
      points: [
        'Names, dates, numbers, percentages, and locations are often strong anchors.',
        'Topic nouns such as migration, pesticide, or legislation are better than vague words like good, change, or important.',
        'Question words such as why or how often are not search words; they tell you the kind of answer you need.',
      ],
    },
    {
      title: 'Keyword vs Paraphrase',
      content:
        '<p>IELTS rarely repeats the whole question exactly. Usually one part stays stable while another part is paraphrased.</p>',
      examples: [
        {
          label: 'Question',
          text: 'Why did the company abandon the project?',
        },
        {
          label: 'Passage',
          text: 'The scheme was eventually dropped because costs escalated beyond the original forecast.',
        },
        {
          label: 'Lesson',
          text: 'abandon = dropped, costs escalated = became more expensive',
        },
      ],
      warning:
        'If you search only for exact words, you will often miss the answer location.',
    },
    {
      title: 'A Fast Search Method',
      content: '<p>Use this sequence when a question feels hard to locate:</p>',
      points: [
        'Underline 2-3 search anchors in the question.',
        'Separate the stable keyword from the idea likely to be paraphrased.',
        'Scan for the stable anchor first, then verify the meaning around it.',
        'If the anchor appears several times, prefer the occurrence closest to the right topic or question set.',
      ],
    },
    {
      title: 'Keyword Traps',
      content: '<p>Some words look useful but waste time:</p>',
      table: {
        headers: ['Weak Search Word', 'Why It Fails', 'Better Alternative'],
        rows: [
          ['important', 'Too general', 'the topic noun or number linked to it'],
          ['people', 'Appears everywhere', 'specific group like volunteers or residents'],
          ['problem', 'Often paraphrased', 'the concrete issue such as flooding or delays'],
          ['increase', 'May appear as rose, grew, climbed', 'pair it with the noun being measured'],
        ],
      },
    },
  ],
  exercises: [
    {
      title: 'Choose the Best Search Anchor',
      type: 'select',
      instructions: 'Choose the strongest keyword or keyword pair for finding the answer.',
      questions: [
        {
          text:
            'Question: "In which year did the glacier begin to retreat more quickly?"\nBest search anchor:',
          options: ['year + glacier', 'begin + quickly', 'in which'],
          answer: 'year + glacier',
          explanation:
            'A date plus the topic noun gives you a strong, visible search target.',
        },
        {
          text:
            'Question: "Why did residents oppose the new airport road?"\nBest search anchor:',
          options: ['residents + airport road', 'why + oppose', 'new + did'],
          answer: 'residents + airport road',
          explanation:
            'The concrete people and topic are better anchors than abstract question words.',
        },
        {
          text:
            'Question: "What percentage of employees worked remotely at least twice a week?"\nBest search anchor:',
          options: ['percentage + remotely', 'worked + week', 'at least + what'],
          answer: 'percentage + remotely',
          explanation:
            'You need the measurable figure and the target behavior.',
        },
      ],
    },
    {
      title: 'Spot the Paraphrase',
      type: 'select',
      instructions: 'Choose the option that best matches the question meaning.',
      questions: [
        {
          text:
            'Question phrase: "was abandoned"\nWhich passage phrase is the closest match?',
          options: ['was dropped', 'was announced', 'was expanded'],
          answer: 'was dropped',
          explanation:
            '"Dropped" is a close paraphrase of "abandoned".',
        },
        {
          text:
            'Question phrase: "rose sharply"\nWhich passage phrase is the closest match?',
          options: ['increased dramatically', 'remained stable', 'was carefully checked'],
          answer: 'increased dramatically',
          explanation:
            'Both phrases express a strong upward movement.',
        },
      ],
    },
    {
      title: 'Type the Best Keyword Pair',
      type: 'fill-in',
      instructions: 'Type the two strongest search anchors separated by a comma.',
      questions: [
        {
          text:
            'Question: "Where were the earliest tools discovered?"',
          answer: 'earliest, tools',
          explanation:
            'The time signal and the topic noun help you search efficiently.',
        },
        {
          text:
            'Question: "How did the researchers measure soil quality?"',
          answer: 'researchers, soil quality',
          explanation:
            'The agent and the topic being measured are strong anchors.',
        },
        {
          text:
            'Question: "Which group benefited most from the revised timetable?"',
          answer: 'group, revised timetable',
          explanation:
            'These two terms point you toward the correct discussion area.',
        },
      ],
    },
    {
      title: 'Explain Your Search Plan',
      type: 'writing',
      instructions: 'Write 1-2 sentences explaining where you would look first and why.',
      questions: [
        {
          text:
            'Question: "What caused delays in the bridge renovation?"\nHow would you search for the answer?',
          example:
            'I would scan for "bridge renovation" first, then look for cause language like because, due to, or caused by in the nearby sentences.',
          tip:
            'A good plan identifies both the topic anchor and the likely paraphrase type.',
        },
        {
          text:
            'Question: "Why did fewer students choose physics in 2021?"\nHow would you search for the answer?',
          example:
            'I would look for 2021 and physics first, then search nearby for decline language like fewer, dropped, or fell, plus an explanation sentence.',
          tip:
            'Think about both the visible anchor and the likely wording of the reason.',
        },
      ],
    },
  ],
  tips: [
    'Choose nouns, names, dates, and numbers before abstract verbs.',
    'Expect at least one key word in the question to be paraphrased in the passage.',
    'Use 2-3 anchors, not one single word, when the topic is broad.',
    'After you find a keyword, always verify the meaning around it.',
  ],
  dailyPractice: [
    'Underline search anchors in three IELTS questions before reading the passage.',
    'Take one article paragraph and list two exact words plus two possible paraphrases.',
    'Practice grouping weak keywords into stronger keyword pairs.',
    'Review missed answers and identify which keyword choice wasted your time.',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext:
    'Keyword Identification: choosing strong search anchors, spotting paraphrases, and locating the right evidence efficiently in IELTS Reading.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...keywordIdentificationData,
      title: keywordIdentificationData.title + ' (Foundations)',
      overview: { ...keywordIdentificationData.overview, duration: '15-20 minutes' },
      sections: keywordIdentificationData.sections.slice(0, 2),
      exercises: [
        {
          ...keywordIdentificationData.exercises[0],
          title: 'Choose the Best Search Anchor (Basic)',
          questions: keywordIdentificationData.exercises[0].questions.slice(0, 2),
        },
        {
          ...keywordIdentificationData.exercises[2],
          title: 'Type the Best Keyword Pair (Basic)',
          questions: keywordIdentificationData.exercises[2].questions.slice(0, 2),
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...keywordIdentificationData,
      title: keywordIdentificationData.title + ' (Advanced)',
      overview: { ...keywordIdentificationData.overview, duration: '30-35 minutes' },
      sections: [
        ...keywordIdentificationData.sections,
        {
          title: 'Advanced Paraphrase Networks',
          content:
            '<p>At higher levels, a single idea can shift across noun, verb, and adjective forms. Train yourself to track meaning through those changes.</p>',
          points: [
            'decline -> declined -> downward trend',
            'decision -> decide -> decisive',
            'restriction -> restricted -> limited',
          ],
        },
      ],
      exercises: [
        ...keywordIdentificationData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        {
          title: 'Advanced Keyword Mapping',
          type: 'select',
          instructions: 'Choose the best anchor set for a heavily paraphrased question.',
          questions: [
            {
              text:
                'Question: "What factor led to the cancellation of the urban farming trial?"\nBest anchor set:',
              options: ['urban farming + cancellation', 'what factor + led', 'trial + what'],
              answer: 'urban farming + cancellation',
              explanation:
                'The concrete topic plus outcome gives you the most reliable search pair.',
            },
          ],
        },
      ],
      tips: [
        ...(keywordIdentificationData.tips || []),
        'At Band 8+, build paraphrase families around your main keyword before scanning.',
        'If a keyword repeats often, add a second anchor that narrows the search field.',
      ],
    };
  }
  return keywordIdentificationData;
}
